import { CircularProgress, Dialog, InputAdornment } from "@mui/material";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { styled } from "@mui/system";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import ContainedButton from "src/components/common/ContainedButton";
import { useDispatch } from "react-redux";
import { ExclamationMarkOutlineRedIcon } from "src/icons/ExclamationMarkOutlineRedIcon";
import { CheckSolidNavyIcon } from "src/icons/CheckSolidNavyIcon";
import { CheckSolidGreenIcon } from "src/icons/CheckSolidGreenIcon";
import { ErrorSolidRedIcon } from "src/icons/ErrorSolidRedIcon";
import { BackOutlineWhiteIcon } from "src/icons/BackOutlineWhiteIcon";
import { CrossOutlineWhiteIcon } from "src/icons/CrossOutlineWhiteIcon";
import { useAccountLockState, STAGES } from "./accountLock.slice";
import { useAccountLockForm } from "./useAccountLock";

interface DialogWrapperProps {
  maxWidth?: boolean;
}
export const DialogWrapper = styled(Dialog)<DialogWrapperProps>(({ maxWidth }) => ({
  display: "flex",
  width: "100%", // Keep this to ensure background overlay covers the entire screen
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "10px",
  cursor: "default",
  background: "rgba(0, 32, 53, 0.7)",

  // this is repeated so that mobile scrollbar is styled too
  "& .MuiDialog-paper": {
    /* Style scrollbar for WebKit (Chrome, Safari) */
    "&::-webkit-scrollbar": {
      width: "10px" /* Width of the scrollbar */,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#E5E7E8" /* Color of the scrollbar thumb */,
      borderRadius: "5px" /* Border radius of the scrollbar thumb */,
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent" /* Color of the scrollbar thumb */,
    },
  },

  // needed to ensure when onfido SDK is open on mobile, the modal stays full width on the screen
  "& .MuiDialog-container": {
    width: "100%",
  },

  // control width of modal for desktop
  ...(maxWidth === false && {
    "& .MuiDialog-paper": {
      width: "500px", // as per design

      /* Style scrollbar for WebKit (Chrome, Safari) */
      "&::-webkit-scrollbar": {
        width: "10px" /* Width of the scrollbar */,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#E5E7E8" /* Color of the scrollbar thumb */,
        borderRadius: "5px" /* Border radius of the scrollbar thumb */,
      },

      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent" /* Color of the scrollbar thumb */,
      },
    },
  }),
}));

export const HeaderWrapper = styled("div")(() => ({
  display: "flex",
  padding: "16px 20px",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  alignSelf: "stretch",
  background: "#002035",
}));

export const HeaderWrapperMobile = styled("div")(() => ({
  display: "flex",
  height: "110px",
  padding: "24px 16px",
  justifyContent: "space-between",
  alignItems: "center",
  flexShrink: 0,
  background: "#002035",
  boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30)",
}));

export const HeaderTitle = styled("div")(() => ({
  flex: "1 0 0",
  color: "#FFF",
  textAlign: "center",
  fontFamily: "Roobert",
  fontSize: "23px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "28px",
  letterSpacing: "-0.46px",
}));

export const HeaderTitleMobile = styled("div")(() => ({
  flex: "1 0 0",
  color: "#FFF",
  textAlign: "center",
  fontFamily: "Roobert",
  fontSize: "23px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "28px",
  letterSpacing: "-0.46px",
}));

export const HeaderIcon = styled("div")(() => ({
  display: "flex",
  width: "16px",
  height: "16px",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
}));

export const HeaderIconMobile = styled("div")(() => ({
  display: "flex",
  width: "24px",
  height: "24px",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
}));

export const ContentWrapper = styled("div")(() => ({
  display: "flex",
  padding: "24px 24px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "24px",
  alignSelf: "stretch",
}));

export const ContentWrapperMobile = styled("div")(() => ({
  display: "flex",
  padding: "0px 16px",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 0 0",
  alignSelf: "stretch",
}));

export const ContentHeadingWrapperMobile = styled("div")(() => ({
  display: "flex",
  padding: "24px 0px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

export const ContentHeadingTitleMobile = styled("div")(() => ({
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "28px",
  fontStyle: "normal",
  fontWeight: "600",
  lineHeight: "34px",
  letterSpacing: "-0.56px",
}));

export const ContentHeadingTypography = styled("div")(() => ({
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "20px",
}));

export const SubContentWrapperMobile = styled("div")(() => ({
  display: "flex",
  paddingBottom: "28px",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
  flex: "1 0 0",
  alignSelf: "stretch",
}));

export const ButtonText = styled("div")(() => ({
  color: "#FFF",
  fontFamily: "Roobert",
  fontSize: "18px",
  fontStyle: "normal",
  fontWeight: "600",
  lineHeight: "22px",
}));

export const LimitationsContentWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
}));

export const PushButtonToBottom = styled("div")(() => ({
  flex: "1 0 0",
}));

export const LimitationsLineWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
}));

export const LimitationsLineTypography = styled("div")(() => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
}));

export const FormInputWrapper = styled("div")<{ isActive: boolean }>(({ isActive }) => ({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  opacity: isActive ? 1 : 0.2,
  pointerEvents: isActive ? "auto" : "none",
}));

export const FormInputHeading = styled("div")<{ includeFlex?: boolean }>(
  ({ includeFlex = true }) => ({
    flex: includeFlex ? "1 0 0" : "none",
    color: "#FC4C4C",
    fontFamily: "Roobert",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "20px",
  }),
);

export const FormInputSubHeading = styled("div")(() => ({
  flex: "1 0 0",
  color: "#5C6469",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
}));

export const FormInputFieldWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

interface FormInputFieldBoxProps {
  hasError: boolean;
  isActive: boolean;
}
export const FormInputFieldBox = styled("div")<FormInputFieldBoxProps>(
  ({ hasError, isActive }) => ({
    display: "flex",
    padding: "12px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "8px",
    border: `1px solid ${hasError ? "#EF4444" : isActive ? "#002035" : "#E5E7E8"}`,
    cursor: "text",
  }),
);

export const FormInputFieldText = styled("input")(() => ({
  flex: "1 0 0",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
  border: "none",
  outline: "none",
  background: "transparent",

  "&::placeholder": {
    color: "#BDC2C5",
    userSelect: "none",
  },

  "&:disabled": {
    background: "transparent",
  },
}));

interface FormInputFieldIconProps {
  hasOnClick?: boolean;
}
export const FormInputFieldIcon = styled("div")<FormInputFieldIconProps>(({ hasOnClick }) => ({
  display: "flex",
  width: "12px",
  height: "12px",
  padding: "1.5px 0px 1.269px 0px",
  justifyContent: "center",
  alignItems: "center",
  ...(hasOnClick && { cursor: "pointer" }),
}));

export const FormInputErrorWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
}));

export const FormInputErrorText = styled("div")(() => ({
  flex: "1 0 0",
  color: "#EF4444",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "14px",
}));

export const BannerContentWrapper = styled("div")<{ background?: string }>(({ background }) => ({
  display: "flex",
  padding: "12px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  alignSelf: "stretch",
  borderRadius: "10px",
  background: background ?? "#F0FEF9",
}));

export const BannerDetails = styled("div")(() => ({
  display: "flex",
  padding: "0px",
  alignItems: "center",
  gap: "12px 12px",
  alignSelf: "stretch",
  flexWrap: "wrap",
}));

export const BannerDetailsText = styled("div")(() => ({
  display: "inline",
  alignItems: "center",
  gap: "12px",
  flex: "1 0 0",
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
}));

export default function ContactChangeModal() {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  const { isAccountLockModalOpen, currentStage } = useAccountLockState();

  const {
    handleClose,
    title,
    handleAcknowledgeClick,
    lockReason,
    errorMsg,
    handleLockReasonChange,
    isLoadingLockAccount,
    handleSubmit,
    lockReasonInputRef,
  } = useAccountLockForm();

  const ModalHeader = () => {
    return !isMobile ? (
      <HeaderWrapper>
        <HeaderTitle>{title}</HeaderTitle>
        <HeaderIcon onClick={handleClose}>
          <CrossOutlineWhiteIcon />
        </HeaderIcon>
      </HeaderWrapper>
    ) : (
      <HeaderWrapperMobile>
        <HeaderIconMobile onClick={handleClose}>
          <BackOutlineWhiteIcon />
        </HeaderIconMobile>
        <HeaderTitleMobile>
          <CalebAndBrownLogo
            height={40}
            width={141}
            color="currentColor"
            aria-label="Caleb & Brown"
          />
        </HeaderTitleMobile>
        {/* Empty Icon to push title to centre */}
        <HeaderIconMobile />
      </HeaderWrapperMobile>
    );
  };

  const LimitationsContent = () => {
    return (
      <LimitationsContentWrapper>
        {currentStage === STAGES.COMPLETED && (
          <LimitationsLineWrapper>
            <LimitationsLineTypography>
              There will be limitations to your account, these limitations are in place until your
              account is unlocked:
            </LimitationsLineTypography>
          </LimitationsLineWrapper>
        )}
        <LimitationsLineWrapper>
          <ErrorSolidRedIcon />
          <LimitationsLineTypography>
            You cannot place any trades, targets or schedule any orders
          </LimitationsLineTypography>
        </LimitationsLineWrapper>
        <LimitationsLineWrapper>
          <ErrorSolidRedIcon />
          <LimitationsLineTypography>You cannot deposit any assets</LimitationsLineTypography>
        </LimitationsLineWrapper>
        <LimitationsLineWrapper>
          <ErrorSolidRedIcon />
          <LimitationsLineTypography>
            You cannot withdraw assets or add any new withdrawal addresses
          </LimitationsLineTypography>
        </LimitationsLineWrapper>
      </LimitationsContentWrapper>
    );
  };

  const AcknowledgeStage = () => {
    return !isMobile ? (
      <ContentWrapper>
        <ContentHeadingTypography>
          Please note that locking your account will limit the following actions:
        </ContentHeadingTypography>
        <LimitationsContent />
        <LimitationsLineTypography>
          Your account can only be unlocked by contacting your broker or support.
        </LimitationsLineTypography>
        <ContainedButton height="46px" color="salmon" onClick={handleAcknowledgeClick}>
          <ButtonText>I acknowledge</ButtonText>
        </ContainedButton>
      </ContentWrapper>
    ) : (
      <ContentWrapperMobile>
        <ContentHeadingWrapperMobile>
          <ContentHeadingTitleMobile>{title}</ContentHeadingTitleMobile>
          <ContentHeadingTypography>
            Please note that locking your account will limit the following actions:
          </ContentHeadingTypography>
        </ContentHeadingWrapperMobile>
        <SubContentWrapperMobile>
          <LimitationsContent />
          <LimitationsLineTypography>
            Your account can only be unlocked by contacting your broker or support.
          </LimitationsLineTypography>
          {/* <PushButtonToBottom /> */}
          <ContainedButton height="46px" color="salmon" onClick={handleAcknowledgeClick}>
            <ButtonText>I acknowledge</ButtonText>
          </ContainedButton>
        </SubContentWrapperMobile>
      </ContentWrapperMobile>
    );
  };

  const FormLockReasonInput = () => {
    return (
      <FormInputWrapper isActive={true}>
        {/* <FormInputHeading>Lock reason</FormInputHeading> */}
        <FormInputSubHeading>Please provide a reason for locking your account</FormInputSubHeading>
        <FormInputFieldWrapper>
          <FormInputFieldBox hasError={errorMsg !== ""} isActive={true}>
            <FormInputFieldText
              id="lock-reason"
              value={lockReason}
              onChange={handleLockReasonChange}
              placeholder="Enter reason here"
              disabled={isLoadingLockAccount}
              ref={lockReasonInputRef}
            />
          </FormInputFieldBox>
        </FormInputFieldWrapper>
      </FormInputWrapper>
    );
  };

  const FormContent = () => {
    return (
      <>
        <FormLockReasonInput />
      </>
    );
  };

  const FormHeader = () => {
    return (
      <ContentHeadingTypography>
        Please provide a reason for locking your account.
      </ContentHeadingTypography>
    );
  };

  const FormConfirmButton = () => {
    return (
      <>
        <ContainedButton
          height="46px"
          color="salmon"
          disabled={isLoadingLockAccount}
          onClick={handleSubmit}
          endIcon={
            <InputAdornment position="end" style={{ width: 20 }}>
              {isLoadingLockAccount && <CircularProgress size={20} style={{ color: "white" }} />}
            </InputAdornment>
          }>
          <ButtonText>Lock account</ButtonText>
        </ContainedButton>
        {errorMsg && (
          <FormInputErrorWrapper>
            <ExclamationMarkOutlineRedIcon />
            <FormInputErrorText>{errorMsg}</FormInputErrorText>
          </FormInputErrorWrapper>
        )}
      </>
    );
  };

  const FormStage = () => {
    return !isMobile ? (
      <ContentWrapper>
        {/* <FormHeader /> */}
        <FormContent />
        {lockReason && <FormConfirmButton />}
      </ContentWrapper>
    ) : (
      <ContentWrapperMobile>
        <ContentHeadingWrapperMobile>
          <ContentHeadingTitleMobile>{title}</ContentHeadingTitleMobile>
          {/* <FormHeader /> */}
        </ContentHeadingWrapperMobile>
        <SubContentWrapperMobile>
          <FormContent />
          {lockReason && <FormConfirmButton />}
        </SubContentWrapperMobile>
      </ContentWrapperMobile>
    );
  };

  const AllStepsCompletedContent = () => {
    return (
      <>
        <BannerContentWrapper>
          <BannerDetails>
            <CheckSolidNavyIcon />
            <BannerDetailsText>Your request to lock your account is successful.</BannerDetailsText>
          </BannerDetails>
        </BannerContentWrapper>
        <LimitationsContent />
        <LimitationsLineTypography style={{ alignSelf: "stretch" }}>
          Please contact us at support@calebandbrown.com to unlock your account.
        </LimitationsLineTypography>
        {/* {isMobile && <PushButtonToBottom />} */}
        <ContainedButton
          height="46px"
          color="salmon"
          onClick={() => {
            dispatch(handleClose);
          }}>
          <ButtonText>Done</ButtonText>
        </ContainedButton>
      </>
    );
  };

  const AllStepsCompletedStage = () => {
    return !isMobile ? (
      <ContentWrapper>
        <AllStepsCompletedContent />
      </ContentWrapper>
    ) : (
      <ContentWrapperMobile>
        <ContentHeadingWrapperMobile>
          <ContentHeadingTitleMobile>{title}</ContentHeadingTitleMobile>
        </ContentHeadingWrapperMobile>
        <SubContentWrapperMobile>
          <AllStepsCompletedContent />
        </SubContentWrapperMobile>
      </ContentWrapperMobile>
    );
  };

  const ModalContent = () => {
    switch (currentStage) {
      case STAGES.ACKNOWLEDGE:
        return <AcknowledgeStage />;
      case STAGES.FORM:
        return <FormStage />;
      case STAGES.COMPLETED:
        return <AllStepsCompletedStage />;
      default:
        return null;
    }
  };

  return (
    <DialogWrapper
      open={isAccountLockModalOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={!isMobile ? false : undefined} // disable modal width limit for desktop
    >
      <ModalHeader />
      <ModalContent />
    </DialogWrapper>
  );
}
